import type { InputAsset } from '$lib/model/types';
import { readDpi } from './exif';

export type SizeClassification =
  | { kind: 'ok' }
  | { kind: 'warn'; megapixels: number }
  | { kind: 'refuse'; megapixels: number };

export const SOFT_LIMIT_MP = 24;
export const HARD_LIMIT_MP = 64;

const ACCEPTED_MIME = new Set(['image/png', 'image/jpeg', 'image/webp']);

export class UploadRejected extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadRejected';
  }
}

export interface DecodedUpload {
  asset: InputAsset;
  bitmap: ImageBitmap;
  blob: Blob;
  classification: SizeClassification;
}

export async function decodeUpload(
  file: File | Blob,
  fallbackFilename?: string,
): Promise<DecodedUpload> {
  if (!ACCEPTED_MIME.has(file.type)) {
    throw new UploadRejected('Unsupported file type. Use PNG, JPEG, or WebP.');
  }

  const filename =
    file instanceof File
      ? file.name
      : (fallbackFilename ?? `pasted-${new Date().toISOString()}.png`);

  // createImageBitmap reads the binary into a decoded image suitable for use
  // in canvas and workers.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new UploadRejected('Could not decode image data.');
  }

  const megapixels = (bitmap.width * bitmap.height) / 1_000_000;
  const classification: SizeClassification =
    bitmap.width * bitmap.height >= HARD_LIMIT_MP * 1_000_000
      ? { kind: 'refuse', megapixels }
      : bitmap.width * bitmap.height >= SOFT_LIMIT_MP * 1_000_000
        ? { kind: 'warn', megapixels }
        : { kind: 'ok' };

  if (classification.kind === 'refuse') {
    bitmap.close();
    throw new UploadRejected(
      `Image is too large (${megapixels.toFixed(1)} MP). Maximum is ${HARD_LIMIT_MP} MP.`,
    );
  }

  const dpi = (await readDpi(file)) ?? 72;

  const asset: InputAsset = {
    filename,
    mime: file.type,
    source_width: bitmap.width,
    source_height: bitmap.height,
    source_dpi: dpi,
  };

  return { asset, bitmap, blob: file, classification };
}

export function extractFromDataTransfer(dt: DataTransfer): File | null {
  if (dt.files.length > 0) {
    const file = dt.files[0];
    if (file) return file;
  }
  for (const item of Array.from(dt.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  return null;
}

export function extractFromClipboardEvent(e: ClipboardEvent): File | null {
  const dt = e.clipboardData;
  if (!dt) return null;
  for (const item of Array.from(dt.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  return null;
}

export function filenameStem(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return filename;
  return filename.slice(0, lastDot);
}
